import { useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';

export interface MeasuredWidthOptions {
  /** A width a suite or the harness hands in; the element is then not observed. */
  override?: number | undefined;
  /** What the first paint stands on, before the element has been measured. */
  initial?: number | null;
  /** The element's client width turned into the width kept, such as a floor or a rounding; a zero reads as unmeasured by default. */
  shape?: (clientWidth: number) => number | null;
}

const UNMEASURED = (clientWidth: number): number | null => clientWidth || null;

/**
 * The element's own width in CSS pixels, read once on mount and again whenever it
 * resizes (T677). 🔨 The one hook behind the list viewer's mode and the balance
 * chart's drawing; a browser with no `ResizeObserver` keeps the initial width.
 */
export function useMeasuredWidth<E extends HTMLElement>({ override, initial = null, shape = UNMEASURED }: MeasuredWidthOptions = {}): [MutableRefObject<E | null>, number | null] {
  const ref = useRef<E | null>(null);
  const [measured, setMeasured] = useState<number | null>(initial);
  useLayoutEffect(() => {
    if (override !== undefined) return;
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const read = () => setMeasured(shape(el.clientWidth));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [override, shape]);
  return [ref, override ?? measured];
}
